import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CompanyPicker from '../components/CompanyPicker';
import { useApi } from '../hooks/useApi';
import { invalidFlavorFields } from '../utils/flavorChecks';
import { requestJson } from '../utils/lifecycle';
import { toast, Toaster } from 'react-hot-toast';
import Config from '../config/Config';
import LifecycleShell from '../components/LifecycleShell';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { fetchJson, getFriendlyErrorMessage } from '../utils/api';

const baseUrl = Config.ApiBaseUrl;
const JSON_HEADERS = { 'Content-Type': 'application/json' };
const BANK_OPTIONS = [
    { code: 'TKI', name: 'Bank Negara Indonesia' },
    { code: 'TKIPAYBSI', name: 'Bank Syariah Indonesia' },
    { code: 'BNI', name: 'Bank Negara Indonesia' },
    { code: 'BNI OGP', name: 'Bank Negara Indonesia' },
    { code: 'BNI 46', name: 'Bank Negara Indonesia' },
    { code: 'BNI SYARIAH', name: 'Bank BNI Syariah' },
    { code: 'BANKJATIM', name: 'Bank Jatim Syariah' },
    { code: 'BANKJATIM OVERBOOK', name: 'Bank Jatim Syariah' },
    { code: 'BSI', name: 'Bank Syariah Indonesia' },
    { code: 'BSI-MAKARA', name: 'Bank Syariah Indonesia' },
    { code: 'BSI-SBI', name: 'Bank Syariah Indonesia' },
    { code: 'NTB SYARIAH', name: 'Bank Ntb Syariah' },
    { code: 'MUAMALAT', name: 'Bank Muamalat' },
    { code: 'BCA', name: 'Bank Central Asia' },
    { code: 'BRI', name: 'Bank Rakyat Indonesia' },
    { code: 'MANDIRI', name: 'Bank Mandiri' },
    { code: 'PERMATA', name: 'Bank Permata' },
    { code: 'PERMATA SYARIAH', name: 'Bank Permata Syariah' },
    { code: 'BJB', name: 'Bank BJB' },
    { code: 'BJB SYARIAH', name: 'Bank BJB Syariah' },
    { code: 'BTN', name: 'Bank BTN' },
    { code: 'BTPN', name: 'Bank BTPN' },
    { code: 'CIMB', name: 'Bank CIMB Niaga' },
    { code: 'DANAMON', name: 'Bank Danamon' },
    { code: 'MAYBANK', name: 'Bank Maybank' },
    { code: 'OCBC', name: 'Bank OCBC NISP' },
    { code: 'SUMSELBABEL', name: 'Bank Sumsel Babel Syariah' },
];

const defaultFlavor = () => ({
    flavorName: '',
    date: new Date().toISOString().slice(0, 10),
    packageName: '',
    applicationName: '',
    versionCode: 1,
    versionName: '0.0.1',
    keystoreName: '',
    keyAlias: '',
    password: '',
    defaultCompanyId: '',
    defaultCompanyName: '',
    allCompanyName: '',
    multiCompanies: false,
    urlPlayStore: '',
    status: true,
    primary: '#111827',
    secondary: '#e5e7eb',
    banks: [],
    serverKey: '1',
});

function FlavorsPage() {
    const { language } = useLanguage();
    const t = translations[language];
    const [items, setItems] = useState([]);
    const [fileSha, setFileSha] = useState('');
    const [sourceMeta, setSourceMeta] = useState({ repo: '', path: '' });
    const [selectedFlavorName, setSelectedFlavorName] = useState('');
    const [draft, setDraft] = useState(defaultFlavor());
    const [search, setSearch] = useState('');
    const [isNew, setIsNew] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSensitive, setShowSensitive] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [showCrmPicker, setShowCrmPicker] = useState(false);
    const [checkingCompanies, setCheckingCompanies] = useState(false);
    const [flavorErrors, setFlavorErrors] = useState([]);
    const mappings = useApi('/flavor-mappings');
    const mappingByFlavor = useMemo(
        () => Object.fromEntries((mappings.data?.items || []).map((mapping) => [mapping.flavorName, mapping])),
        [mappings.data],
    );
    const mappingStatusOf = (flavor) => mappingByFlavor[flavor.flavorName]?.status || (flavor.defaultCompanyId ? 'unchecked' : 'not_found');

    const getUserFriendlyError = useCallback((error, fallbackMessage) => getFriendlyErrorMessage(error, {
        auth: t.authError,
        conflict: t.conflictError,
        network: t.networkError,
        server: t.networkError,
        generic: fallbackMessage,
    }), [t.authError, t.conflictError, t.networkError]);

    const filteredItems = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return items;
        return items.filter((item) =>
            item.flavorName.toLowerCase().includes(keyword) ||
            item.applicationName.toLowerCase().includes(keyword)
        );
    }, [items, search]);

    const hasUnsavedChanges = useMemo(() => JSON.stringify(draft) !== JSON.stringify(
        isNew ? defaultFlavor() : (items.find((item) => item.flavorName === selectedFlavorName) || defaultFlavor())
    ), [draft, isNew, items, selectedFlavorName]);

    const loadFlavors = useCallback(async (preferredFlavorName = '') => {
        setIsLoading(true);
        setLoadError('');
        try {
            const data = await fetchJson(`${baseUrl}admin/flavors`);
            const nextItems = data.items || [];
            setItems(nextItems);
            setFileSha(data.fileSha || '');
            setSourceMeta({
                repo: data.repo || '',
                path: data.path || '',
            });

            if (preferredFlavorName) {
                const matchedFlavor = nextItems.find((item) => item.flavorName === preferredFlavorName);
                if (matchedFlavor) {
                    setSelectedFlavorName(matchedFlavor.flavorName);
                    setDraft({
                        ...matchedFlavor,
                        banks: matchedFlavor.banks || [],
                    });
                    setIsNew(false);
                    return;
                }
            }

            if (nextItems[0]) {
                setSelectedFlavorName(nextItems[0].flavorName);
                setDraft({
                    ...nextItems[0],
                    banks: nextItems[0].banks || [],
                });
                setIsNew(false);
            } else {
                handleCreateNew();
            }
        } catch (error) {
            const friendlyMessage = getUserFriendlyError(error, t.loadFailed);
            setLoadError(friendlyMessage);
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [getUserFriendlyError, t.loadFailed]);

    // Load once on mount. loadFlavors changes with the UI language, and reloading then would drop unsaved edits.
    useEffect(() => {
        loadFlavors('');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreateNew = () => {
        setSelectedFlavorName('');
        setDraft(defaultFlavor());
        setIsNew(true);
        setShowSensitive(false);
    };

    const handleSelectFlavor = (item) => {
        setSelectedFlavorName(item.flavorName);
        setDraft({
            ...item,
            banks: item.banks || [],
        });
        setIsNew(false);
    };

    const updateDraft = (field, value) => {
        setDraft((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleDuplicate = () => {
        const clone = {
            ...draft,
            flavorName: `${draft.flavorName}-copy`,
            packageName: `${draft.packageName}.copy`,
            applicationName: `${draft.applicationName} Copy`,
        };
        setDraft(clone);
        setSelectedFlavorName('');
        setIsNew(true);
    };

    const normalizeFlavor = () => ({
        ...draft,
        flavorName: draft.flavorName.trim(),
        packageName: draft.packageName.trim(),
        applicationName: draft.applicationName.trim(),
        versionCode: Number(draft.versionCode),
        versionName: draft.versionName.trim(),
        keystoreName: draft.keystoreName.trim(),
        keyAlias: draft.keyAlias.trim(),
        password: draft.password,
        defaultCompanyId: draft.defaultCompanyId.trim(),
        defaultCompanyName: draft.defaultCompanyName.trim(),
        allCompanyName: draft.allCompanyName.trim(),
        urlPlayStore: draft.urlPlayStore.trim(),
        primary: draft.primary.trim(),
        secondary: draft.secondary.trim(),
        banks: draft.banks.filter(Boolean).map((bank) => bank.trim()).filter(Boolean),
        serverKey: draft.serverKey.trim(),
    });

    const checkCompanies = async () => {
        setCheckingCompanies(true);
        try {
            await requestJson('/flavor-mappings/validate', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({}) });
            await mappings.refresh();
            toast.success(t.companiesChecked);
        } catch (error) {
            if (error.status !== 401) toast.error(getUserFriendlyError(error, t.checkCompaniesFailed));
        } finally {
            setCheckingCompanies(false);
        }
    };

    const handleSubmit = async () => {
        const flavor = normalizeFlavor();
        const invalid = invalidFlavorFields(flavor);
        setFlavorErrors(invalid);
        if (invalid.length) return;
        setIsSaving(true);
        const endpoint = isNew ? `${baseUrl}admin/flavors` : `${baseUrl}admin/flavors/${encodeURIComponent(selectedFlavorName)}`;
        const method = isNew ? 'POST' : 'PUT';

        try {
            const data = await fetchJson(endpoint, {
                method,
                headers: JSON_HEADERS,
                body: JSON.stringify({ flavor, fileSha }),
            });
            toast.success(t.savedSuccessfully);
            setLoadError('');
            await loadFlavors(data.flavor.flavorName);
        } catch (error) {
            toast.error(getUserFriendlyError(error, t.saveFailed));
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedFlavorName || !window.confirm(t.confirmDeleteFlavor)) {
            return;
        }

        setIsSaving(true);
        try {
            await fetchJson(`${baseUrl}admin/flavors/${encodeURIComponent(selectedFlavorName)}`, {
                method: 'DELETE',
                headers: JSON_HEADERS,
                body: JSON.stringify({ fileSha }),
            });

            toast.success(t.deletedSuccessfully);
            setLoadError('');
            await loadFlavors('');
        } catch (error) {
            toast.error(getUserFriendlyError(error, t.deleteFailed));
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const fieldId = (field) => `flavor-${field}`;

    const renderInput = (label, field, type = 'text', placeholder = '', wide = false) => (
        <div className={`field${wide ? ' field--wide' : ''}`}>
            <label className="label" htmlFor={fieldId(field)}>{label}</label>
            <input
                id={fieldId(field)}
                type={type}
                value={draft[field]}
                placeholder={placeholder}
                onChange={(event) => updateDraft(field, event.target.value)}
                className="input"
            />
        </div>
    );

    const renderColorInput = (label, field, placeholder = '') => (
        <div className="field">
            <label className="label" htmlFor={fieldId(field)}>{label}</label>
            <div className="color-field">
                <input
                    type="color"
                    aria-label={label}
                    value={/^#[0-9A-Fa-f]{6}$/.test(draft[field]) ? draft[field] : '#000000'}
                    onChange={(event) => updateDraft(field, event.target.value)}
                />
                <input
                    id={fieldId(field)}
                    type="text"
                    value={draft[field]}
                    placeholder={placeholder}
                    onChange={(event) => updateDraft(field, event.target.value)}
                    className="input"
                />
            </div>
        </div>
    );

    const renderSecretInput = (label, field, wide = false) => (
        <div className={`field${wide ? ' field--wide' : ''}`}>
            <label className="label" htmlFor={fieldId(field)}>{label}</label>
            <div className="color-field">
                <input
                    id={fieldId(field)}
                    type={showSensitive ? 'text' : 'password'}
                    value={draft[field]}
                    onChange={(event) => updateDraft(field, event.target.value)}
                    className="input"
                />
                <button type="button" onClick={() => setShowSensitive((prev) => !prev)} className="btn">
                    {showSensitive ? t.hide : t.reveal}
                </button>
            </div>
        </div>
    );

    const toggleBank = (bankCode) => {
        const nextBanks = draft.banks.includes(bankCode)
            ? draft.banks.filter((bank) => bank !== bankCode)
            : [...draft.banks, bankCode];
        updateDraft('banks', nextBanks);
    };

    const renderBankSelector = () => (
        <div className="field field--wide">
            <span className="label" id="flavor-banks-label">{t.banks}</span>
            <div className="form-grid" role="group" aria-labelledby="flavor-banks-label">
                {BANK_OPTIONS.map((bank) => (
                    <button
                        key={bank.code}
                        type="button"
                        onClick={() => toggleBank(bank.code)}
                        className="chip-toggle"
                        aria-pressed={draft.banks.includes(bank.code)}
                    >
                        <strong>{bank.code}</strong>
                        <small className="row__meta">{bank.name}</small>
                    </button>
                ))}
            </div>
            <p className="hint">{draft.banks.length > 0 ? draft.banks.join(', ') : t.arrayHint}</p>
        </div>
    );

    return (
        <LifecycleShell eyebrow={t.flavorsAdmin} title={t.flavorsTitle}>
            <Toaster position="top-right" />
            <section className="card stack" aria-label={t.githubSource}>
                <div className="toolbar">
                    <div className="stack stack--sm">
                        <p className="card__sub">{t.githubSource}</p>
                        <div className="cluster">
                            <span className="chip">{t.fileVersion}: {fileSha ? fileSha.slice(0, 7) : '-'}</span>
                            <span className="chip">{sourceMeta.repo || '-'}</span>
                            <span className="chip">{sourceMeta.path || '-'}</span>
                        </div>
                    </div>
                    <div className="btn-row">
                        <button onClick={() => loadFlavors('')} disabled={isLoading} className="btn" type="button">{t.refreshData}</button>
                        <button onClick={checkCompanies} disabled={checkingCompanies} className="btn" type="button">
                            {checkingCompanies ? t.checkingCompanies : t.checkCompanies}
                        </button>
                    </div>
                </div>
            </section>

            {loadError && (
                <div className="notice notice--error" role="alert">
                    <strong>{loadError}</strong>
                    <span>{t.genericLoadHelp}</span>
                </div>
            )}

            <div className="split split--list">
                <aside className="card stack" aria-label={t.flavorsAdmin}>
                    <div className="toolbar">
                        <p className="card__sub">{t.totalFlavors.replace('{count}', items.length)}</p>
                        <button onClick={handleCreateNew} className="btn btn--primary btn--pill" type="button">{t.createFlavor}</button>
                    </div>
                    <input
                        type="search"
                        value={search}
                        placeholder={t.searchFlavorPlaceholder}
                        aria-label={t.searchFlavorPlaceholder}
                        onChange={(event) => setSearch(event.target.value)}
                        className="input"
                    />
                    <div className="row-list">
                        {filteredItems.map((item) => {
                            const isActive = !isNew && item.flavorName === selectedFlavorName;
                            return (
                                <button
                                    key={item.flavorName}
                                    type="button"
                                    onClick={() => handleSelectFlavor(item)}
                                    className={`row${isActive ? ' row--selected' : ''}`}
                                    aria-pressed={isActive}
                                >
                                    <span className="row__main">
                                        <span className="row__title">{item.applicationName}</span>
                                        <span className="row__meta">{item.flavorName} · v{item.versionName} / {item.versionCode} / server {item.serverKey}</span>
                                        <span className={`row__meta${mappingStatusOf(item) === 'connected' ? '' : ' row__meta--warn'}`}>{t.mappingLabel[mappingStatusOf(item)]}</span>
                                    </span>
                                    <span className="mark" style={{ background: item.primary || undefined }} aria-hidden="true" />
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <section className="card stack stack--lg" aria-labelledby="flavor-editor-heading">
                    <div className="card__head">
                        <div>
                            <p className="page-head__eyebrow">{isNew ? t.createNewFlavor : t.editingFlavor}</p>
                            <h2 id="flavor-editor-heading">{isNew ? draft.applicationName || t.createNewFlavor : draft.applicationName || selectedFlavorName}</h2>
                            <p className="card__sub">{hasUnsavedChanges ? t.unsavedChanges : t.noFlavorSelected}</p>
                        </div>
                        <div className="btn-row">
                            {!isNew && <button type="button" onClick={handleDuplicate} className="btn">{t.duplicateFlavor}</button>}
                            {!isNew && <button type="button" onClick={handleDelete} disabled={isSaving} className="btn btn--danger">{t.deleteFlavor}</button>}
                            <button type="button" onClick={handleSubmit} disabled={isSaving} className="btn btn--primary">{isNew ? t.createRecord : t.saveChanges}</button>
                        </div>
                    </div>

                    {flavorErrors.length > 0 && (
                        <div className="notice notice--error" role="alert">
                            <strong>{t.flavorChecksTitle}</strong>
                            <ul>{flavorErrors.map((field) => <li key={field}>{t.flavorChecks[field]}</li>)}</ul>
                        </div>
                    )}

                    <div className="tile stack">
                        <h3>{t.generalInfo}</h3>
                        <div className="form-grid">
                            {renderInput(t.flavorName, 'flavorName')}
                            {renderInput(t.applicationName, 'applicationName')}
                            {renderInput(t.packageName, 'packageName')}
                            {renderInput(t.date, 'date', 'date')}
                            {renderInput(t.versionCode, 'versionCode', 'number')}
                            {renderInput(t.versionName, 'versionName')}
                        </div>
                    </div>

                    <div className="tile stack">
                        <h3>{t.companyInfo}</h3>
                        <div className="form-grid">
                            {renderInput(t.defaultCompanyId, 'defaultCompanyId')}
                            {renderInput(t.defaultCompanyName, 'defaultCompanyName')}
                            <div className="field field--wide">
                                <button type="button" className="btn btn--pill" onClick={() => setShowCrmPicker((open) => !open)} aria-expanded={showCrmPicker}>
                                    {showCrmPicker ? t.hideCrmPicker : t.chooseFromCrm}
                                </button>
                            </div>
                            {showCrmPicker && <div className="field--wide">
                                <CompanyPicker
                                    id="flavor-company-search"
                                    selected={draft.defaultCompanyId ? { id: draft.defaultCompanyId, name: draft.defaultCompanyName } : null}
                                    onSelect={(company) => {
                                        updateDraft('defaultCompanyId', company.id);
                                        updateDraft('defaultCompanyName', company.name);
                                        setShowCrmPicker(false);
                                    }}
                                />
                            </div>}
                            <div className="field field--wide">
                                <label className="label" htmlFor={fieldId('allCompanyName')}>{t.allCompanyName}</label>
                                <textarea
                                    id={fieldId('allCompanyName')}
                                    rows="3"
                                    value={draft.allCompanyName}
                                    onChange={(event) => updateDraft('allCompanyName', event.target.value)}
                                    className="textarea"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="tile stack">
                        <h3>{t.distributionInfo}</h3>
                        <div className="form-grid">
                            {renderInput(t.urlPlayStore, 'urlPlayStore')}
                            {renderInput(t.serverKey, 'serverKey')}
                            {renderColorInput(t.primaryColor, 'primary', '#111827')}
                            {renderColorInput(t.secondaryColor, 'secondary', '#e5e7eb')}
                            <label className="toggle-row">
                                <span>{t.activeStatus}</span>
                                <input type="checkbox" checked={draft.status} onChange={(event) => updateDraft('status', event.target.checked)} />
                            </label>
                            <label className="toggle-row">
                                <span>{t.multiCompanies}</span>
                                <input type="checkbox" checked={draft.multiCompanies} onChange={(event) => updateDraft('multiCompanies', event.target.checked)} />
                            </label>
                            {renderBankSelector()}
                        </div>
                    </div>

                    <div className="tile stack">
                        <h3>{t.sensitiveInfo}</h3>
                        <div className="form-grid">
                            {renderSecretInput(t.keystoreName, 'keystoreName')}
                            {renderSecretInput(t.keyAlias, 'keyAlias')}
                            {renderSecretInput(t.password, 'password', true)}
                        </div>
                    </div>
                </section>
            </div>
        </LifecycleShell>
    );
}

export default FlavorsPage;
